/**
 * AI Activity Generator (Refactored)
 * Orchestrates AI-powered activity generation
 *
 * Refactored from 548 LOC to component-based architecture
 */

import React, { useState } from 'react';
import { BookOpen, Calculator, Microscope } from 'lucide-react';
import { supabase, Database } from '../lib/supabase';
import { GeneratorHeader } from './ai-generator/GeneratorHeader';
import { ProgressIndicator } from './ai-generator/ProgressIndicator';
import { AIInfoBanner } from './ai-generator/AIInfoBanner';
import { SubjectSelector, Subject } from './ai-generator/SubjectSelector';
import { CurriculumTargetSelector, CurriculumTarget } from './ai-generator/CurriculumTargetSelector';
import { LearningObjectivesForm } from './ai-generator/LearningObjectivesForm';
import { CustomPromptInput } from './ai-generator/CustomPromptInput';
import { GenerationButtons } from './ai-generator/GenerationButtons';
import { UploadTargetModal } from './ai-generator/UploadTargetModal';
import { addObjective, updateObjective, removeObjective } from './ai-generator/objectiveHelpers';

type Child = Database['public']['Tables']['children']['Row'];

export interface AIActivityGeneratorProps {
  child: Child;
  onActivityGenerated: (activityId: string) => void;
  onClose: () => void;
}

interface GenerationProgress {
  step: number;
  totalSteps: number;
  message: string;
  isComplete: boolean;
}

const SUBJECTS: Subject[] = [
  { id: 'math', name: 'Math', icon: Calculator, color: 'blue' },
  { id: 'reading', name: 'Reading', icon: BookOpen, color: 'green' },
  { id: 'science', name: 'Science', icon: Microscope, color: 'purple' },
];

export function AIActivityGenerator({ child, onActivityGenerated, onClose }: AIActivityGeneratorProps) {
  // Form state
  const [selectedSubject, setSelectedSubject] = useState<'math' | 'reading' | 'science'>('math');
  const [curriculumTargets, setCurriculumTargets] = useState<CurriculumTarget[]>([]);
  const [selectedTarget, setSelectedTarget] = useState<string>('');
  const [customPrompt, setCustomPrompt] = useState('');
  const [learningObjectives, setLearningObjectives] = useState<string[]>(['']);

  // Generation state
  const [isGenerating, setIsGenerating] = useState(false);
  const [progress, setProgress] = useState<GenerationProgress>({
    step: 0,
    totalSteps: 6,
    message: '',
    isComplete: false
  });

  // Upload modal state
  const [showUpload, setShowUpload] = useState(false);
  const [uploadForm, setUploadForm] = useState({
    title: '',
    description: '',
    subject: 'math' as 'math' | 'reading' | 'science' | 'general',
    targetLevel: '',
    objectives: ['']
  });

  // Fetch curriculum targets
  const fetchCurriculumTargets = React.useCallback(async () => {
    const { data } = await supabase
      .from('curriculum_targets')
      .select('*')
      .eq('user_id', child.user_id)
      .order('created_at', { ascending: false });

    if (data) {
      setCurriculumTargets(data);
    }
  }, [child.user_id]);

  React.useEffect(() => {
    fetchCurriculumTargets();
  }, [fetchCurriculumTargets]);

  // Safety check for required props
  if (!child) {
    console.error('AIActivityGenerator: child prop is required');
    return (
      <div className="p-6 text-center text-red-600">
        <p>Error: Child data is required</p>
        <button onClick={onClose} className="mt-4 px-4 py-2 bg-gray-600 text-white rounded-lg">
          Close
        </button>
      </div>
    );
  }

  const updateProgress = (step: number, message: string, isComplete: boolean = false) => {
    setProgress({ step, totalSteps: 6, message, isComplete });
  };

  const handleGenerateActivity = async () => {
    setIsGenerating(true);

    try {
      // Progress simulation with delays
      updateProgress(1, 'Connecting to AI brain...');
      await new Promise(resolve => setTimeout(resolve, 800));

      updateProgress(2, `Analyzing ${child.name}'s learning profile...`);
      await new Promise(resolve => setTimeout(resolve, 1000));

      updateProgress(3, `Processing ${selectedSubject} curriculum...`);
      await new Promise(resolve => setTimeout(resolve, 1200));

      updateProgress(4, 'AI is creating personalized questions...');
      await new Promise(resolve => setTimeout(resolve, 1000));

      updateProgress(5, 'Generating your custom activity...');

      // API call
      const apiUrl = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/generate-activity`;

      const response = await fetch(apiUrl, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          childId: child.id,
          subject: selectedSubject,
          curriculumTargetId: selectedTarget || undefined,
          customPrompt: customPrompt || undefined,
          learningObjectives: learningObjectives.filter(obj => obj.trim() !== '')
        })
      });

      const result = await response.json();

      if (result.success && result.activity) {
        updateProgress(6, 'Activity created successfully!', true);
        await new Promise(resolve => setTimeout(resolve, 1000));

        // Reset form
        setSelectedSubject('math');
        setSelectedTarget('');
        setCustomPrompt('');
        setLearningObjectives(['']);
        setIsGenerating(false);
        setProgress({ step: 0, totalSteps: 6, message: '', isComplete: false });

        onActivityGenerated(result.activity.id);
      } else {
        throw new Error(result.error || 'Failed to generate activity');
      }
    } catch (error) {
      console.error('Error generating activity:', error);
      setProgress({
        step: 0,
        totalSteps: 6,
        message: 'Failed to generate activity. Please try again.',
        isComplete: false
      });
      setTimeout(() => {
        setIsGenerating(false);
        setProgress({ step: 0, totalSteps: 6, message: '', isComplete: false });
      }, 2000);
    }
  };

  const handleUploadTarget = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      const { data, error } = await supabase
        .from('curriculum_targets')
        .insert({
          child_id: child.id,
          title: uploadForm.title,
          description: uploadForm.description,
          subject: uploadForm.subject,
          target_level: uploadForm.targetLevel,
          learning_objectives: uploadForm.objectives.filter(obj => obj.trim() !== '')
        })
        .select()
        .single();

      if (error) throw error;

      setCurriculumTargets(prev => [data, ...prev]);
      setShowUpload(false);
      setUploadForm({
        title: '',
        description: '',
        subject: 'math',
        targetLevel: '',
        objectives: ['']
      });
    } catch (error) {
      console.error('Error uploading curriculum target:', error);
      alert('Failed to save curriculum target. Please try again.');
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <GeneratorHeader
          childName={child.name}
          isGenerating={isGenerating}
          onClose={onClose}
        />

        {isGenerating && <ProgressIndicator progress={progress} />}

        <div className="p-6 space-y-6">
          <AIInfoBanner childName={child.name} />

          <SubjectSelector
            subjects={SUBJECTS}
            selectedSubject={selectedSubject}
            onSelectSubject={(id) => setSelectedSubject(id as 'math' | 'reading' | 'science')}
            disabled={isGenerating}
          />

          <CurriculumTargetSelector
            targets={curriculumTargets}
            selectedTarget={selectedTarget}
            onSelectTarget={setSelectedTarget}
            onAddTarget={() => setShowUpload(true)}
            disabled={isGenerating}
          />

          <LearningObjectivesForm
            objectives={learningObjectives}
            onAdd={() => addObjective(learningObjectives, setLearningObjectives)}
            onUpdate={(index, value) => updateObjective(index, value, learningObjectives, setLearningObjectives)}
            onRemove={(index) => removeObjective(index, learningObjectives, setLearningObjectives)}
            disabled={isGenerating}
          />

          <CustomPromptInput
            value={customPrompt}
            onChange={setCustomPrompt}
            disabled={isGenerating}
          />

          <GenerationButtons
            isGenerating={isGenerating}
            childName={child.name}
            onCancel={onClose}
            onGenerate={handleGenerateActivity}
          />
        </div>

        <UploadTargetModal
          show={showUpload && !isGenerating}
          form={uploadForm}
          onFormChange={setUploadForm}
          onSubmit={handleUploadTarget}
          onClose={() => setShowUpload(false)}
        />
      </div>
    </div>
  );
}
